import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useApiData } from '../hooks/useApiData';
import { useTier } from '../context/TierContext';
import SeatCount from '../components/SeatCount';
import {
  ClientCell,
  MetricBar,
  ScoreCell,
  StatusPill,
  LoadingState,
  ErrorState,
  EmptyState
} from '../components/primitives';
import RangeControl, { DEFAULT_RANGE, rangeLabel } from '../components/RangeControl';
import { codeOf, fmtInt, fmtMoney } from '../lib/format';
import FailedClientsNote from '../components/FailedClientsNote';
import { ClientRangeActivity, ClientRow, TimeRange } from '../types/superadmin';

/** A client's current state joined to its range activity (absent while that loads). */
type TableRow = ClientRow & { range?: ClientRangeActivity };

// Columns that come from the range-activity API; the rest are current state.
const CURRENT_STATE_KEYS: ReadonlyArray<string> = ['openTickets', 'mrr', 'riskScore'];

type SortKey =
  | 'name'
  | 'sessions'
  | 'minutes'
  | 'objFaqScore'
  | 'landmineScore'
  | 'competitorScore'
  | 'callQualityScore'
  | 'trainingQualityScore'
  | 'trainingCompletion'
  | 'openTickets'
  | 'mrr'
  | 'riskScore';

// 'bar' columns render a 0-100 bar; 'score' render a compact number; the rest
// are plain numbers/money. Dimension scores read the rep-pooled aggregate.
type ColKind = 'score' | 'bar' | 'int' | 'minutes' | 'money';
const COLUMNS: Array<{ key: SortKey; label: string; kind: ColKind }> = [
  { key: 'sessions', label: 'Sessions', kind: 'int' },
  { key: 'minutes', label: 'Minutes', kind: 'minutes' },
  { key: 'objFaqScore', label: 'Obj/FAQ', kind: 'score' },
  { key: 'landmineScore', label: 'Landmine', kind: 'score' },
  { key: 'competitorScore', label: 'Competitor', kind: 'score' },
  { key: 'callQualityScore', label: 'Call quality', kind: 'bar' },
  { key: 'trainingQualityScore', label: 'Training quality', kind: 'bar' },
  { key: 'trainingCompletion', label: 'Training completion', kind: 'bar' },
  { key: 'openTickets', label: 'Tickets', kind: 'int' },
  { key: 'mrr', label: 'MRR', kind: 'money' }
];

// Fixed Client col + metric COLUMNS + KB, Plan, Seats, Risk.
const TOTAL_COLS = 1 + COLUMNS.length + 4;

// Sort accessor; null scores sort last regardless of direction.
const sortVal = (r: TableRow, key: SortKey): number | null => {
  switch (key) {
    case 'sessions':
      return r.range?.activity?.sessions ?? null;
    case 'minutes':
      return r.range?.activity?.minutes ?? null;
    case 'objFaqScore':
      return r.range?.dimensions?.objFaqScore ?? null;
    case 'landmineScore':
      return r.range?.dimensions?.landmineScore ?? null;
    case 'competitorScore':
      return r.range?.dimensions?.competitorScore ?? null;
    case 'callQualityScore':
      return r.range?.dimensions?.callQualityScore ?? null;
    case 'trainingQualityScore':
      return r.range?.dimensions?.trainingQualityScore ?? null;
    case 'trainingCompletion':
      return r.range?.dimensions?.trainingCompletion ?? null;
    case 'openTickets':
      return r.openTickets;
    case 'mrr':
      return r.mrr;
    case 'riskScore':
      return r.riskScore;
    default:
      return 0;
  }
};

// Render one metric cell by its column kind.
function MetricCell({ row, col }: { row: TableRow; col: { key: SortKey; kind: ColKind } }) {
  if (!row.range && !CURRENT_STATE_KEYS.includes(col.key)) return <span className="caption">…</span>;
  const v = sortVal(row, col.key);
  switch (col.kind) {
    case 'score':
      return <ScoreCell value={v} />;
    case 'bar':
      return <MetricBar value={v} />;
    case 'minutes':
      return (
        <span className="metric-num">
          {fmtInt(v || 0)}
          <span className="u">min</span>
        </span>
      );
    case 'money':
      return <span className="metric-num">{fmtMoney(v || 0)}</span>;
    case 'int':
    default:
      return (
        <span className={'metric-num' + (col.key === 'openTickets' && (v || 0) > 0 ? ' warn' : '')}>
          {fmtInt(v || 0)}
        </span>
      );
  }
}

export default function Clients() {
  const [range, setRange] = useState<TimeRange>(DEFAULT_RANGE);
  const { tier } = useTier();
  // Current state (seats, KB, plan, risk) and range activity load separately.
  const { data, loading, error } = useApiData(() => api.getClients(tier), [tier]);
  const activityState = useApiData(() => api.getClientsActivity(range, tier), [JSON.stringify(range), tier]);
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [atRiskOnly, setAtRiskOnly] = useState(false);
  // Default ordering is by risk profile (most flags first); the Client column
  // header toggles alphabetical-by-name ordering.
  const [sortKey, setSortKey] = useState<SortKey>('riskScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  const rows: TableRow[] = useMemo(() => {
    const byTenant = activityState.data?.data?.clients ?? {};
    return (data?.data?.clients ?? []).map((client) => ({ ...client, range: byTenant[client.tenantId] }));
  }, [data, activityState.data]);

  const visible = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = rows.filter((r) => {
      if (atRiskOnly && !r.isAtRisk) return false;
      if (!term) return true;
      return r.displayName.toLowerCase().includes(term) || r.tenantId.toLowerCase().includes(term);
    });
    return [...filtered].sort((a, b) => {
      // Name sorts alphabetically; every other column sorts numerically with
      // null scores pushed to the bottom regardless of direction.
      if (sortKey === 'name') {
        const cmp = a.displayName.localeCompare(b.displayName, undefined, { sensitivity: 'base' });
        return sortDir === 'desc' ? -cmp : cmp;
      }
      const av = sortVal(a, sortKey);
      const bv = sortVal(b, sortKey);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === 'desc' ? bv - av : av - bv;
    });
  }, [rows, search, atRiskOnly, sortKey, sortDir]);

  if (loading) return <LoadingState />;
  if (error) return <ErrorState message={error} />;

  const setSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(sortDir === 'desc' ? 'asc' : 'desc');
    else {
      setSortKey(k);
      // Name reads best A→Z first; numeric columns read best high-to-low first.
      setSortDir(k === 'name' ? 'asc' : 'desc');
    }
  };
  const arrow = (k: SortKey) => (sortKey === k ? (sortDir === 'desc' ? '↓' : '↑') : '↕');
  const thCls = (k: SortKey) => 'sortable' + (sortKey === k ? ' sorted' : '');
  const atRiskCount = rows.filter((r) => r.isAtRisk).length;

  return (
    <>
      <div className="head">
        <div>
          <div className="eyebrow">Clients · {rangeLabel(range)}</div>
          <h1 className="title">
            <span className="serif">Every client, one table</span>
          </h1>
          <p className="subtitle">
            {rows.length} clients · {atRiskCount} at risk. Score columns are the average across the
            client's reps for the selected period; plan, seats, KB and risk are current. Click a row
            to drill in.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <RangeControl value={range} onChange={setRange} />
        </div>
      </div>

      <FailedClientsNote failed={[...(data?.meta?.failed ?? []), ...(activityState.data?.meta?.failed ?? [])]} />
      {activityState.error && <ErrorState message={activityState.error} />}

      <div className="filter-bar">
        <span className="label">Search</span>
        <input
          className="search-input"
          placeholder="Client name or id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <span className="sep" />
        <button className={'filter-chip' + (atRiskOnly ? ' on' : '')} onClick={() => setAtRiskOnly((v) => !v)}>
          At risk only<span className="c">{atRiskCount}</span>
        </button>
        <span className="grow" />
        <span className="freshness">{visible.length} shown</span>
      </div>

      <div className="tbl tbl-scroll">
        <table>
          <thead>
            <tr>
              <th style={{ width: '18%' }} className={thCls('name')} onClick={() => setSort('name')}>
                Client <span className="sort-arr">{arrow('name')}</span>
              </th>
              {COLUMNS.map((c) => (
                <th key={c.key} className={thCls(c.key)} onClick={() => setSort(c.key)}>
                  {c.label} <span className="sort-arr">{arrow(c.key)}</span>
                </th>
              ))}
              <th>KB</th>
              <th>Plan</th>
              <th>Seats</th>
              <th className={thCls('riskScore')} onClick={() => setSort('riskScore')}>
                Risk <span className="sort-arr">{arrow('riskScore')}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visible.map((r: TableRow) => (
              <tr
                key={r.tenantId}
                className="clickable"
                onClick={() => navigate(`/clients/${r.tenantId}`)}
              >
                <td>
                  <ClientCell name={r.displayName} code={codeOf(r.tenantId)} sub={r.tenantId} />
                </td>
                {COLUMNS.map((c) => (
                  <td key={c.key}>
                    <MetricCell row={r} col={c} />
                  </td>
                ))}
                <td>
                  {r.kbComplete ? (
                    <span className="chip pos">Complete</span>
                  ) : (
                    <span className="chip neg" title={r.kbMissing.join(', ')}>
                      {r.kbMissing.length} gap{r.kbMissing.length === 1 ? '' : 's'}
                    </span>
                  )}
                </td>
                <td>
                  <StatusPill status={r.subscriptionStatus} />
                </td>
                <td>
                  <span className="mono" style={{ color: 'var(--ink-muted)' }}>
                    <SeatCount used={r.seatsUsed} total={r.seatsTotal} isTrial={r?.trialAccessEnabled === true} />
                  </span>
                </td>
                <td>
                  {r.isAtRisk ? (
                    <span className="chip neg">{r.riskScore} flag{r.riskScore === 1 ? '' : 's'}</span>
                  ) : (
                    <span className="chip pos">OK</span>
                  )}
                </td>
              </tr>
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={TOTAL_COLS}>
                  <EmptyState>
                    No clients match. <b>Adjust your search</b> or clear the at-risk filter.
                  </EmptyState>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
